import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ClipboardListIcon, FileEditIcon, SearchIcon, FilterIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const stepIcons = [<ClipboardListIcon className="w-6 h-6" data-id="element-382" />, <FileEditIcon className="w-6 h-6" data-id="element-383" />, <SearchIcon className="w-6 h-6" data-id="element-384" />, <FilterIcon className="w-6 h-6" data-id="element-385" />, <VideoIcon className="w-6 h-6" data-id="element-386" />, <CheckCircleIcon className="w-6 h-6" data-id="element-387" />];
const stepNumbers = ['01', '02', '03', '04', '05', '06'];
export function ProcessSteps() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="process" data-id="element-388">
      <div className="absolute inset-0 pointer-events-none" data-id="element-389">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-[500px] h-[500px] rounded-full bg-primary-50/30 blur-3xl" data-id="element-390" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-cyan-50/25 blur-3xl" data-id="element-391" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-392">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-393">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-394">
            {t.process.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-395">
            {t.process.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-396">
            {t.process.subtitle}
          </p>
        </motion.div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden lg:block" data-id="element-397">
          <div className="relative" data-id="element-398">
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: 'easeOut'
          }} className="absolute top-12 left-[8%] right-[8%] h-1 bg-gradient-to-r from-primary-300 via-primary-500 to-primary-300 origin-left rounded-full" data-id="element-399" />
            <div className="grid grid-cols-6 gap-4" data-id="element-400">
              {t.process.steps.map((step, index) => <motion.div key={stepNumbers[index]} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.12
            }} className="flex flex-col items-center text-center" data-id="element-401">
                  <div className="relative z-10" data-id="element-402">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-lg shadow-primary-500/25" data-id="element-403">
                      {stepIcons[index]}
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-lg bg-white border-2 border-primary-200 flex items-center justify-center shadow-sm" data-id="element-404">
                      <span className="text-xs font-extrabold text-primary-600" data-id="element-405">
                        {stepNumbers[index]}
                      </span>
                    </div>
                  </div>
                  <h3 className="mt-5 text-base font-bold text-navy-900" data-id="element-406">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-navy-500 leading-relaxed" data-id="element-407">
                    {step.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: vertical timeline */}
        <div className="lg:hidden space-y-0" data-id="element-408">
          {t.process.steps.map((step, index) => <motion.div key={stepNumbers[index]} initial={{
          opacity: 0,
          x: -20
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="relative flex gap-5 pb-10 last:pb-0" data-id="element-409">
              {index < t.process.steps.length - 1 && <div className="absolute left-7 top-16 bottom-0 w-0.5 bg-gradient-to-b from-primary-400 to-primary-200" data-id="element-410" />}
              <div className="relative z-10 flex-shrink-0" data-id="element-411">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-lg shadow-primary-500/20" data-id="element-412">
                  {stepIcons[index]}
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-md bg-white border-2 border-primary-200 flex items-center justify-center" data-id="element-413">
                  <span className="text-[10px] font-extrabold text-primary-600" data-id="element-414">
                    {stepNumbers[index]}
                  </span>
                </div>
              </div>
              <div className="pt-1" data-id="element-415">
                <h3 className="text-lg font-bold text-navy-900" data-id="element-416">
                  {step.title}
                </h3>
                <p className="mt-1 text-sm text-navy-500 leading-relaxed" data-id="element-417">
                  {step.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}